<!-- HighChartsWrapper.vue -->
<template>
  <div ref="highchartswrapper">
    <highcharts
      :key="componentKey"
      ref="highcharts"
      :constructor-type="constructorType"
      :options="chartOptionsWithListeners"
      :callback="scheduleImageUpdate"
    />

    <ul
      v-if="['pie'].includes(chartType)"
      class="tw-flex tw-w-full tw-justify-stretch"
    >
      <li
        v-for="text in seriesNames"
        :key="text"
        class="tw-grow tw-basis-0 tw-text-center tw-font-bold"
      >
        <span>{{ text }}</span>
      </li>
    </ul>
  </div>
</template>

<script>
import { Canvg } from 'canvg';
import { Chart } from 'highcharts-vue';
import Highcharts from 'highcharts';
import stockInit from 'highcharts/modules/stock';
import annotationModule from 'highcharts/modules/annotations';
import exportingModule from 'highcharts/modules/exporting';
import sankeyModule from 'highcharts/modules/sankey';
import more from 'highcharts/highcharts-more';

more(Highcharts);
stockInit(Highcharts);
annotationModule(Highcharts);
exportingModule(Highcharts);
sankeyModule(Highcharts);

import { useCheckupStore } from '@frontend/ui/stores/checkupStore';
import { mapStores } from 'pinia';

export default {
  name: 'HighChartsWrapper',
  components: { highcharts: Chart },
  props: {
    name: {
      type: String,
      default: 'Chart',
    },
    chartOptions: {
      type: Object,
      required: true,
    },
    chartType: {
      type: String,
      default: 'line',
    },
    exportImage: {
      type: Boolean,
      default: false,
    },
    customText: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      id: null,
      imageUpdateTimeout: null,
      height: 0,
      width: 0,
    };
  },
  computed: {
    ...mapStores(useCheckupStore),

    constructorType() {
      return this.chartType !== 'line' ? 'chart' : 'stockChart';
    },
    componentKey() {
      return this.constructorType + Date.now();
    },
    aspectRatio() {
      if (this.height === 0) return 0;
      return this.width / this.height;
    },
    seriesNames() {
      return this.chartOptions.series.map(series => series.name);
    },
    seriesCount() {
      return this.chartOptions.series?.length || 0;
    },
    chartOptionsWithListeners() {
      const options = { ...this.chartOptions };

      options.chart = {
        ...options.chart,
        events: {
          render: () => {
            this.scheduleImageUpdate();
          },
          click: event => {
            console.log('Chart click event:', event);
            this.handleChartClick(event);
          },
        },
      };

      if ('type' in options.chart && options.chart['type'] === 'pie') {
        options.chart.height = 350;
      }

      if (this.chartType === 'sankey' || 
          (options.series && options.series.some(s => s.type === 'sankey'))) {
        options.chart.height = options.chart.height || 500;
      }

      if (!options.plotOptions) {
        options.plotOptions = {};
      }

      options.plotOptions.pie = {
        ...options.plotOptions.pie,
        size: "100%",
        dataLabels: {
          enabled: false, // Disable data labels
        },
        connectorPadding: 5,
        softConnector: true,
      };

      options.plotOptions.series = {
        ...options.plotOptions.series,
        cursor: 'pointer',
        events: {
          click: event => {
            this.handleSeriesPointClick(event);
          },
        },
      };

      if (this.chartType !== 'sankey' && 
          !(options.series && options.series.some(s => s.type === 'sankey'))) {
        const xAxis = options.xAxis || {};
        xAxis.events = {
          afterSetExtremes: e => {
            this.$emit('x-range-changed', e);
          },
        };
        options.xAxis = xAxis;
      }

      return options;
    },
  },
  watch: {
    'checkupStore.activeArea': {
      handler() {
        if (this.name === this.checkupStore.activeArea) this.updateDimensions();
      },
      immediate: true,
    },
  },
  created() {
    this.id = 'image-' + this.createID();
  },
  mounted() {
    this.updateDimensions();
  },
  destroyed() {
    window.clearTimeout(this.imageUpdateTimeout);
    this.$emit('image-removed', { id: this.id, name: this.name });
  },
  methods: {
    createID() {
      return Math.random().toString(36).substring(2, 9);
    },
    updateDimensions() {
      const wrapper = this.$refs.highchartswrapper;
      this.height = wrapper?.offsetHeight || 0;
      this.width = wrapper?.offsetWidth || 0;
    },
    getSVG() {
      const component = this.$refs.highcharts;

      // Set the sourceHeight dynamically based on chartType
      let sourceHeight = 400;
      if (this.chartType === 'pie') {
        sourceHeight = 350;
      } else if (this.chartType === 'sankey') {
        sourceHeight = 500;
      }

      const plotOptions = {
        pie: { size: '100%' },
      };
      if (this.chartType === 'sankey') {
        plotOptions.sankey = {
          nodeWidth: 20,
          nodePadding: 20,
        };
      }

      const svg = component.chart.getSVG({
        rangeSelector: { enabled: false },
        navigator: { enabled: false },
        scrollbar: { enabled: false },
        exporting: { sourceWidth: 800, sourceHeight: sourceHeight },
        plotOptions,
      });
      return svg;
    },
    async toCanvas(svg) {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      const canvg = Canvg.fromString(ctx, svg);
      await canvg.render();
      return canvas;
    },
    async getImageMetadata(src) {
      console.log('HighChartsWrapper Just inside getImageMetadata');
      const img = new Image();
      console.log('HighChartsWrapper Created an empty Image object');
      img.src = src;
      console.log('HighChartsWrapper Set the image source');
      await img.decode();
      console.log('HighChartsWrapper Decoded the image');
      return img;
    },
    async toImage() {
      if (!this.exportImage) return;
      console.log('Updating highcharts image');

      let svg; let canvas; let base64String; let aspectRatio;
      try {
        svg = this.getSVG();
        canvas = await this.toCanvas(svg);
        base64String = canvas.toDataURL('image/png');
        console.log('HighChartsWrapper Base64:', { snippet: base64String.slice(0, 100), length: base64String.length });
        const { naturalWidth, naturalHeight } = await this.getImageMetadata(base64String);
        console.log('HighChartsWrapper Image metadata:', { naturalWidth, naturalHeight });
        aspectRatio = naturalWidth / naturalHeight;
        console.log('HighChartsWrapper Aspect ratio:', aspectRatio);
        //
      } catch (error) {
        console.error('Error while updating highcharts image', {
          error,
          svg,
          canvas,
          base64String,
          aspectRatio,
        });
        return;
      }

      this.$emit('image-changed', { id: this.id, image: { base64String, aspectRatio } });
      window.clearTimeout(this.imageUpdateTimeout);
      this.imageUpdateTimeout = null;
    },
    scheduleImageUpdate() {
      if (this.imageUpdateTimeout) return;
      window.clearTimeout(this.imageUpdateTimeout);
      this.imageUpdateTimeout = window.setTimeout(() => this.toImage(), 500);
    },
    handleChartClick(event) {
      console.log('This is what happens when you click the background of a chart', event);
    },
    handleSeriesPointClick(event) {
      console.log('This has information about the series data on click (like which portfolio was clicked)', event);
      // Emit the point data to the parent component
      const pointData = {
        label: event.point.name || event.point.category,
        value: {
          label: event.point.series.name,
        },
      };
      this.$emit('seriesPointClick', pointData);
    },
  },
};
</script>

<style scoped></style>
